import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getBuyerOrders, uploadVoucher, giftOrder, listingOrder } from '@/api';
import type { BuyerOrder } from '@/api';
import SignatureModal from '@/components/SignatureModal';

const tabs = ['全部', '待签名', '待确认', '可挂卖', '已完成', '已取消'];

export default function BuyerWarehouse() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryTab = searchParams.get('tab') || '全部';
  const [activeTab, setActiveTab] = useState(tabs.includes(queryTab) ? queryTab : '全部');
  const [orders, setOrders] = useState<BuyerOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [uploadingId, setUploadingId] = useState<string | null>(null);
  const [signTarget, setSignTarget] = useState<BuyerOrder | null>(null); //待签名确认的订单
  const [giftTarget, setGiftTarget] = useState<BuyerOrder | null>(null);
  const [giftPhone, setGiftPhone] = useState('');
  const [gifting, setGifting] = useState(false);
  const [listingTarget, setListingTarget] = useState<BuyerOrder | null>(null);
  const [listinging, setListinging] = useState(false);
  const listingLockRef = useRef(false);//同步锁：防连续点击重复提交（state 更新有延迟，快速连点会绕过）
  const [rates, setRates] = useState({ consignment: 1.5, storage: 1.5 });
  const [isBoss, setIsBoss] = useState(false); //老板账号（is_member=1）才显示转增按钮
  const [toast, setToast] = useState('');
  const tabContainerRef = useRef<HTMLDivElement>(null);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2000);
  };

  const loadOrders = useCallback(async () => {
    try {
      const res = await getBuyerOrders(0, 1, 50);
      setOrders(res.list || []);
      if (res.rates) setRates(res.rates);
      setIsBoss(res.isBoss === true); //老板账号标识：转增按钮仅老板可见
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const filtered =
    activeTab === '全部'
      ? orders
      : orders.filter((o) => o.status === activeTab);

  const scrollToTab = (label: string) => {
    setActiveTab(label);
    const idx = tabs.findIndex((t) => t === label);
    if (tabContainerRef.current && idx >= 0) {
      const tabEl = tabContainerRef.current.children[idx] as HTMLElement;
      if (tabEl) {
        const containerWidth = tabContainerRef.current.offsetWidth;
        const tabLeft = tabEl.offsetLeft;
        const tabWidth = tabEl.offsetWidth;
        tabContainerRef.current.scrollTo({
          left: tabLeft - containerWidth / 2 + tabWidth / 2,
          behavior: 'smooth',
        });
      }
    }
  };

  /** 待签名订单：电子签名确认付款（签名图作为凭证提交） */
  const handleSignConfirm = async (base64: string) => {
    if (!signTarget) return;
    setUploadingId(signTarget.id);
    try {
      await uploadVoucher(Number(signTarget.id), base64, '');
      showToast('签名提交成功');
      setSignTarget(null);
      await loadOrders();
    } catch (err) {
      showToast(err instanceof Error ? err.message : '提交失败');
    } finally {
      setUploadingId(null);
    }
  };

  /** 可挂卖订单挂卖：确认后生成新挂卖单（新挂卖价格=购买价格×(1+(代卖费率+保管费率)/100)） */
  const handleListing = async () => {
    if (!listingTarget || listingLockRef.current) return;
    listingLockRef.current = true;
    setListinging(true);
    try {
      const res = await listingOrder(Number(listingTarget.id));
      showToast(`挂卖成功，新挂卖价 ¥${Number(res.listing_price).toFixed(2)}`);
      setListingTarget(null);
      await loadOrders();
    } catch (err) {
      showToast(err instanceof Error ? err.message : '挂卖失败');
    } finally {
      listingLockRef.current = false;
      setListinging(false);
    }
  };

  /** 可挂卖订单转赠：弹层输入手机号，转给对方 */
  const openGiftModal = (order: BuyerOrder) => {
    setGiftPhone('');
    setGiftTarget(order);
  };

  const handleGiftSubmit = async () => {
    if (gifting || !giftTarget) return;
    if (!/^1\d{10}$/.test(giftPhone.trim())) {
      showToast('请输入正确的手机号');
      return;
    }
    setGifting(true);
    try {
      await giftOrder(Number(giftTarget.id), giftPhone.trim());
      showToast('转赠成功');
      setGiftTarget(null);
      await loadOrders();
    } catch (err) {
      showToast(err instanceof Error ? err.message : '转赠失败');
    } finally {
      setGifting(false);
    }
  };

  return (
    <div className="mobile-container bg-background-50 min-h-screen pb-28">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-background-200/50">
        <div className="flex items-center px-4 h-12">
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 rounded-lg flex items-center justify-center text-foreground-700 cursor-pointer hover:bg-background-100 active:scale-95 transition-all"
            aria-label="返回"
          >
            <i className="ri-arrow-left-line text-xl" />
          </button>
          <h1 className="flex-1 text-center text-foreground-950 text-base font-bold tracking-wide">
            买单仓库
          </h1>
          <div className="w-9" />
        </div>
      </header>

      {/* Operation flow card */}
      <div className="mx-4 mt-4 p-4 rounded-2xl bg-secondary-50/60 border border-secondary-200/30">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-6 h-6 rounded-md bg-secondary-100 flex items-center justify-center">
            <i className="ri-book-open-line text-secondary-600 text-xs" />
          </div>
          <p className="text-foreground-900 text-sm font-bold">操作流程</p>
        </div>
        <div className="space-y-2.5">
          <div className="flex items-center gap-2.5">
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary-100 text-primary-600 text-[10px] font-bold flex-shrink-0">1</span>
            <span className="text-foreground-500 text-xs">9:30 进货（体验商家 9:25）</span>
          </div>
          <div className="flex items-center gap-2.5">
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary-100 text-primary-600 text-[10px] font-bold flex-shrink-0">2</span>
            <span className="text-foreground-500 text-xs">电子签名确认付款</span>
          </div>
          <div className="flex items-center gap-2.5">
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary-100 text-primary-600 text-[10px] font-bold flex-shrink-0">3</span>
            <span className="text-foreground-500 text-xs">等待卖家确认</span>
          </div>
          <div className="flex items-center gap-2.5">
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary-100 text-primary-600 text-[10px] font-bold flex-shrink-0">4</span>
            <span className="text-foreground-500 text-xs">周一至周五点击"转拍"（批量流转溢价%）</span>
          </div>
        </div>
      </div>

      {/* Tabs - pill style */}
      <div className="sticky top-12 z-30 bg-background-50/95 backdrop-blur-sm px-4 pt-3 pb-2">
        <div
          ref={tabContainerRef}
          className="flex overflow-x-auto gap-2 scrollbar-hide"
        >
          {tabs.map((label) => {
            const isActive = activeTab === label;
            return (
              <button
                key={label}
                onClick={() => scrollToTab(label)}
                className={`flex-shrink-0 px-4 py-2 rounded-full text-xs font-bold cursor-pointer transition-all duration-300 whitespace-nowrap ${
                  isActive
                    ? 'bg-primary-500 text-white shadow-md shadow-primary-500/15'
                    : 'bg-white text-foreground-500 border border-background-200/60 hover:bg-background-100 hover:text-foreground-700'
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Order list */}
      <main className="px-4 pt-3 space-y-4">
        {loading && (
          <div className="flex flex-col items-center justify-center py-20">
            <i className="ri-loader-4-line text-primary-400 text-3xl animate-spin mb-3" />
            <p className="text-foreground-400 text-sm">加载中...</p>
          </div>
        )}
        {error && !loading && (
          <div className="text-center py-20">
            <i className="ri-wifi-off-line text-foreground-300 text-3xl mb-3 block" />
            <p className="text-foreground-400 text-sm">{error}</p>
          </div>
        )}
        {!loading && !error && filtered.map((order, index) => (
          <div
            key={order.id}
            className="bg-white rounded-2xl overflow-hidden shadow-sm border border-background-200/40"
            style={{ animationDelay: `${index * 80}ms` }}
          >
            {/* Order header */}
            <div className="flex items-center justify-between px-4 py-3 bg-background-50/50">
              <div className="flex items-center gap-2">
                <i className="ri-file-list-3-line text-foreground-300 text-sm" />
                <span className="text-foreground-400 text-xs font-mono tracking-wide">
                  {order.orderNo}
                </span>
              </div>
              <span className={`px-3 py-1 text-[10px] font-bold rounded-full ${
                order.status === '待签名'
                  ? 'bg-orange-50 text-orange-600 border border-orange-200/50'
                  : order.status === '可挂卖'
                    ? 'bg-cyan-50 text-cyan-600 border border-cyan-200/50'
                    : order.status === '已完成'
                      ? 'bg-green-50 text-green-600 border border-green-200/50'
                      : order.status === '已取消'
                        ? 'bg-red-50 text-red-500 border border-red-200/50'
                        : 'bg-background-100 text-foreground-500 border border-background-200/50'
              }`}>
                {order.status}
              </span>
            </div>

            {/* Product info */}
            <div className="px-4 py-4 flex gap-3">
              <div className="w-[72px] h-[72px] rounded-xl overflow-hidden flex-shrink-0 bg-background-100 ring-1 ring-background-200/50">
                {order.productImage ? (
                  <img
                    src={order.productImage}
                    alt={order.productName}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <i className="ri-image-line text-foreground-300 text-xl" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0 flex flex-col justify-between">
                <div>
                  <p className="text-foreground-900 text-sm font-bold">
                    {order.productName}
                  </p>
                  <p className="text-primary-500 text-base font-bold mt-0.5">
                    ¥{Number(order.price).toLocaleString()}
                  </p>
                </div>
                <div className="flex flex-col gap-1 mt-1.5">
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 text-[10px] font-bold bg-foreground-800 text-white rounded">
                      卖家
                    </span>
                    <span className="text-foreground-500 text-xs">{order.sellerName}</span>
                    <span className="text-foreground-300 text-xs">{order.sellerPhone}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 text-[10px] font-bold bg-primary-500 text-white rounded">
                      买家
                    </span>
                    <span className="text-foreground-500 text-xs">{order.buyerName}</span>
                    <span className="text-foreground-300 text-xs">{order.buyerPhone}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Time */}
            <div className="px-4 pb-2">
              <p className="text-foreground-300 text-xs flex items-center gap-1">
                <i className="ri-time-line text-[10px]" />
                进货时间：{order.purchaseTime}
              </p>
            </div>

            {/* Divider */}
            <div className="mx-4 h-px bg-background-200/60" />

            {/* Payment section：兑换订单无签名环节，整块隐藏 */}
            {!order.isExchange && (
            <div className="px-4 py-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-1.5">
                  <i className="ri-image-line text-foreground-400 text-xs" />
                  <span className="text-foreground-500 text-xs font-medium">
                    {order.status === '待签名' && !order.paymentImage ? '请先签名' : '签名图片'}
                  </span>
                </div>
                <span className="text-accent-500 text-xs font-bold flex items-center gap-1">
                  <i className="ri-checkbox-circle-line text-sm" />
                  {order.paymentStatus}
                </span>
              </div>

              {order.paymentImage ? (
                <div
                  className="relative rounded-xl overflow-hidden bg-background-50 ring-1 ring-background-200/50 cursor-pointer group"
                  onClick={() => setPreviewImage(order.paymentImage)}
                >
                  <img
                    src={order.paymentImage}
                    alt="签名图片"
                    className="w-full h-auto max-h-56 object-contain"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors" />
                  <div className="absolute bottom-3 right-3 w-9 h-9 rounded-full bg-black/30 backdrop-blur-sm flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity">
                    <i className="ri-zoom-in-line text-sm" />
                  </div>
                </div>
              ) : order.status === '待签名' ? (
                <button
                  onClick={() => setSignTarget(order)}
                  disabled={uploadingId === order.id}
                  className="w-full py-6 rounded-xl border-2 border-dashed border-primary-300 bg-primary-50/50 flex flex-col items-center justify-center gap-1.5 cursor-pointer hover:bg-primary-50 transition-colors disabled:opacity-60"
                >
                  <i className="ri-quill-pen-line text-primary-400 text-2xl" />
                  <span className="text-primary-500 text-xs font-bold">
                    {uploadingId === order.id ? '提交中...' : '签名确认付款'}
                  </span>
                  <span className="text-foreground-400 text-[10px]">手写签名代替上传付款截图</span>
                </button>
              ) : (
                <div className="py-6 rounded-xl bg-background-50 flex flex-col items-center justify-center gap-1">
                  <i className="ri-image-off-line text-foreground-300 text-xl" />
                  <span className="text-foreground-300 text-xs">暂无签名图片</span>
                </div>
              )}
              {order.paymentImage && (
                <p className="text-center text-foreground-300 text-xs mt-2.5">
                  点击图片可放大查看
                </p>
              )}
            </div>
            )}

            {/* Footer amount */}
            <div className="px-4 py-3 bg-background-50/50 border-t border-background-200/30 flex justify-end items-center gap-1">
              <i className="ri-checkbox-circle-line text-accent-500 text-sm" />
              <span className="text-accent-600 text-sm font-bold">
                {order.status}
              </span>
              <span className="text-accent-500 text-lg font-bold ml-1">
                ¥{Number(order.finalAmount).toLocaleString()}
              </span>
            </div>

            {/* 可挂卖订单操作：挂卖 / 转增（仅老板账号显示转增） */}
            {order.status === '可挂卖' && (
              <div className="px-4 py-3 bg-background-50/50 flex gap-3">
                <button
                  onClick={() => setListingTarget(order)}
                  className="flex-1 py-2.5 rounded-xl bg-primary-500 text-white text-sm font-bold flex items-center justify-center gap-1.5 cursor-pointer hover:bg-primary-600 active:scale-[0.98] transition-all"
                >
                  <i className="ri-store-2-line" />
                  挂卖
                </button>
                {isBoss && (
                  <button
                    onClick={() => openGiftModal(order)}
                    className="flex-1 py-2.5 rounded-xl bg-white text-primary-500 border border-primary-200 text-sm font-bold flex items-center justify-center gap-1.5 cursor-pointer hover:bg-primary-50 active:scale-[0.98] transition-all"
                  >
                    <i className="ri-gift-line" />
                    转增
                  </button>
                )}
              </div>
            )}
          </div>
        ))}

        {!loading && !error && filtered.length === 0 && (
          <div className="text-center py-20">
            <div className="w-16 h-16 rounded-2xl bg-background-100 flex items-center justify-center mx-auto mb-4">
              <i className="ri-inbox-2-line text-foreground-300 text-2xl" />
            </div>
            <p className="text-foreground-400 text-sm font-medium">暂无订单</p>
            <p className="text-foreground-300 text-xs mt-1">切换其他标签页看看吧</p>
          </div>
        )}
      </main>

      {/* Floating bottom button */}
      <div className="fixed bottom-0 left-0 right-0 z-40 px-4 py-4 bg-white/80 backdrop-blur-md border-t border-background-200/50">
        <button
          onClick={() => navigate('/purchase')}
          className="w-full h-12 rounded-xl bg-primary-500 text-white text-sm font-bold flex items-center justify-center gap-2 cursor-pointer hover:bg-primary-600 active:scale-[0.98] transition-all shadow-lg shadow-primary-500/20"
        >
          <i className="ri-shopping-cart-line" />
          去进货
        </button>
      </div>

      {/* Image preview modal */}
      {previewImage && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-6"
          onClick={() => setPreviewImage(null)}
        >
          <div className="relative max-w-full max-h-full">
            <img
              src={previewImage}
              alt="签名图片放大"
              className="max-w-full max-h-[80vh] rounded-xl object-contain"
            />
            <button
              className="absolute -top-10 right-0 w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white cursor-pointer hover:bg-white/30 transition-colors"
              onClick={() => setPreviewImage(null)}
            >
              <i className="ri-close-line text-lg" />
            </button>
          </div>
        </div>
      )}

      {/* Gift modal：转赠给好友 */}
      {giftTarget && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => !gifting && setGiftTarget(null)}>
          <div className="w-full max-w-sm bg-white rounded-2xl p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-9 h-9 rounded-xl bg-primary-50 flex items-center justify-center">
                <i className="ri-gift-line text-primary-500 text-lg" />
              </div>
              <h3 className="text-foreground-900 text-base font-bold">转增给好友</h3>
            </div>

            {/* 订单摘要 */}
            <div className="flex items-center gap-3 p-3 rounded-xl bg-background-50 border border-background-200/40 mb-4">
              <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 bg-background-100">
                {giftTarget.productImage ? (
                  <img src={giftTarget.productImage} alt={giftTarget.productName} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <i className="ri-image-line text-foreground-300" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-foreground-900 text-sm font-bold truncate">{giftTarget.productName}</p>
                <p className="text-primary-500 text-sm font-bold mt-0.5">¥{Number(giftTarget.price).toLocaleString()}</p>
              </div>
            </div>

            {/* 手机号输入 */}
            <label className="block text-foreground-500 text-xs font-medium mb-1.5">对方手机号</label>
            <input
              type="tel"
              maxLength={11}
              value={giftPhone}
              onChange={(e) => setGiftPhone(e.target.value.replace(/\D/g, ''))}
              placeholder="请输入接收方的手机号"
              disabled={gifting}
              className="w-full h-11 px-3 rounded-xl bg-background-50 border border-background-200/60 text-foreground-900 text-sm tracking-wide placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
            />
            <p className="text-foreground-400 text-[11px] mt-1.5 flex items-center gap-1">
              <i className="ri-information-line text-xs" />
              转增后订单将归属对方，不可撤回
            </p>

            {/* 操作按钮 */}
            <div className="flex gap-3 mt-5">
              <button
                onClick={() => setGiftTarget(null)}
                disabled={gifting}
                className="flex-1 py-2.5 rounded-xl bg-white text-foreground-500 border border-background-200/60 text-sm font-bold cursor-pointer hover:bg-background-100 active:scale-[0.98] transition-all disabled:opacity-60"
              >
                取消
              </button>
              <button
                onClick={handleGiftSubmit}
                disabled={gifting}
                className="flex-1 py-2.5 rounded-xl bg-primary-500 text-white text-sm font-bold flex items-center justify-center gap-1.5 cursor-pointer hover:bg-primary-600 active:scale-[0.98] transition-all disabled:opacity-60"
              >
                {gifting && <i className="ri-loader-4-line animate-spin" />}
                {gifting ? '转赠中...' : '确认转增'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Listing modal：确认挂卖 */}
      {listingTarget && (() => {
        const purchase = Number(listingTarget.price);
        const listingPrice = +(purchase * (1 + (rates.consignment + rates.storage) / 100)).toFixed(2);
        return (
          <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => !listinging && setListingTarget(null)}>
            <div className="w-full max-w-sm bg-white rounded-2xl p-5" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-9 h-9 rounded-xl bg-primary-50 flex items-center justify-center">
                  <i className="ri-store-2-line text-primary-500 text-lg" />
                </div>
                <h3 className="text-foreground-900 text-base font-bold">确认挂卖</h3>
              </div>

              {/* 订单摘要 */}
              <div className="flex items-center gap-3 p-3 rounded-xl bg-background-50 border border-background-200/40 mb-4">
                <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 bg-background-100">
                  {listingTarget.productImage ? (
                    <img src={listingTarget.productImage} alt={listingTarget.productName} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <i className="ri-image-line text-foreground-300" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-foreground-900 text-sm font-bold truncate">{listingTarget.productName}</p>
                  <p className="text-foreground-400 text-xs mt-0.5">{listingTarget.orderNo}</p>
                </div>
              </div>

              {/* 价格明细 */}
              <div className="rounded-xl bg-background-50 border border-background-200/40 px-4 py-3 mb-4">
                <div className="flex justify-between items-center text-xs text-foreground-500">
                  <span>购买价格</span>
                  <span className="font-bold text-foreground-800">¥{purchase.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center text-xs text-foreground-500 mt-2.5">
                  <span>代卖费率(%)</span>
                  <span className="text-accent-600 font-medium">+{rates.consignment}%</span>
                </div>
                <div className="flex justify-between items-center text-xs text-foreground-500 mt-2.5">
                  <span>保管费率(%)</span>
                  <span className="text-accent-600 font-medium">+{rates.storage}%</span>
                </div>
                <div className="border-t border-dashed border-background-200 mt-3 pt-3 flex justify-between items-center">
                  <span className="text-foreground-800 text-sm font-bold">新挂卖价格</span>
                  <span className="text-primary-500 text-lg font-bold">¥{listingPrice.toLocaleString()}</span>
                </div>
              </div>

              <p className="text-foreground-400 text-[11px] mb-4 flex items-center gap-1">
                <i className="ri-information-line text-xs" />
                确认后将生成新的挂卖单，等待他人抢购
              </p>

              {/* 操作按钮 */}
              <div className="flex gap-3">
                <button
                  onClick={() => setListingTarget(null)}
                  disabled={listinging}
                  className="flex-1 py-2.5 rounded-xl bg-white text-foreground-500 border border-background-200/60 text-sm font-bold cursor-pointer hover:bg-background-100 active:scale-[0.98] transition-all disabled:opacity-60"
                >
                  取消
                </button>
                <button
                  onClick={handleListing}
                  disabled={listinging}
                  className="flex-1 py-2.5 rounded-xl bg-primary-500 text-white text-sm font-bold flex items-center justify-center gap-1.5 cursor-pointer hover:bg-primary-600 active:scale-[0.98] transition-all disabled:opacity-60"
                >
                  {listinging && <i className="ri-loader-4-line animate-spin" />}
                  {listinging ? '挂卖中...' : '确认挂卖'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* 电子签名弹窗（待签名订单确认付款） */}
      <SignatureModal
        visible={!!signTarget}
        orderNo={signTarget?.orderNo || ''}
        finalAmount={Number(signTarget?.finalAmount || 0)}
        submitting={!!signTarget && uploadingId === signTarget.id}
        onClose={() => setSignTarget(null)}
        onConfirm={handleSignConfirm}
      />

      {/* Toast */}
      {toast && (
        <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 px-5 py-2.5 bg-black/70 backdrop-blur-sm rounded-2xl text-white text-sm z-50 shadow-xl">
          {toast}
        </div>
      )}
    </div>
  );
}
